import { useState } from "react";
import { PrimaryButton } from "../Buttons";
import {
  FaBitcoin,
  FaEthereum,
  FaChevronDown,
  FaUniversity,
  FaCheckCircle,
  FaMobileAlt,
} from "react-icons/fa";
import useCurrency from "../../hooks/useCurrency";

export const PageOne = ({
  setPages,
  selectedToken,
  setSelectedToken,
  amount,
  setAmount,
  usdAmount,
  setUsdAmount,
}) => {
  const {
    getCurrencySymbol,
    systemCurrency,
    convertUSDToDisplay,
    convertDisplayToUSD,
    isLoaded,
  } = useCurrency();

  const [toggleSelectState, setToggleSelectState] = useState(false);
  const [error, setError] = useState("");
  const minimunDeposit = 153.7;

  const tokensArray = [
    {
      tokenName: "Bank Transfer",
      shortName: "BANK",
      address: "Contact Support",
      network: "Fiat",
      icon: FaUniversity,
      color: "text-purple-600",
      bgColor: "bg-purple-100",
    },
    {
      tokenName: "Airtel Money",
      shortName: "AIRTEL",
      address: "Contact Support",
      network: "Mobile Money",
      icon: FaMobileAlt,
      color: "text-red-600",
      bgColor: "bg-red-100",
    },
    {
      tokenName: "MTN Money",
      shortName: "MTN",
      address: "Contact Support",
      network: "Mobile Money",
      icon: FaMobileAlt,
      color: "text-yellow-600",
      bgColor: "bg-yellow-100",
    },
    {
      tokenName: "Bitcoin (BTC)",
      shortName: "BTC",
      address: "1QFdWcgw217WK6mCYfq64eXTeGYvhe5hFg",
      network: "Bitcoin",
      icon: FaBitcoin,
      color: "text-orange-500",
      bgColor: "bg-orange-100",
    },
    {
      tokenName: "USDT (TRC20)",
      shortName: "USDT",
      address: "TV6G3EEm1mEqH9SnxVqBJ5R2QwtHPm4qaZ",
      network: "Tron",
      icon: FaEthereum,
      color: "text-green-500",
      bgColor: "bg-green-100",
    },
    {
      tokenName: "Ethereum (ETH)",
      shortName: "ETH",
      address: "0x7c9a7f217a56743eb58c4cb2245278579bea6721",
      network: "Ethereum",
      icon: FaEthereum,
      color: "text-blue-500",
      bgColor: "bg-blue-100",
    },
  ];

  const handleSelectToken = (token) => {
    setSelectedToken({ ...selectedToken, ...token });
    setToggleSelectState(false);
  };

  const handleAmountChange = (e) => {
    if (!isLoaded) return;
    const val = Number(e.target.value);
    const min = convertUSDToDisplay(minimunDeposit);

    if (val < min && val > 0) {
      setError(`Minimum is ${getCurrencySymbol()}${min.toFixed(0)}`);
    } else {
      setError("");
    }

    setAmount(val);
    setUsdAmount(convertDisplayToUSD(val));
  };

  return (
    <div className="p-6 md:p-8">
      <form className="space-y-8">
        {/* Token Selector */}
        <div className="relative">
          <label className="block text-xs font-bold text-gray-500 uppercase tracking-wide mb-2">
            Select Asset
          </label>

          <button
            type="button"
            onClick={() => setToggleSelectState(!toggleSelectState)}
            className="w-full flex items-center justify-between p-4 bg-gray-50 border border-gray-200 rounded-2xl hover:bg-white hover:border-primary transition-all duration-200 group"
          >
            {selectedToken.tokenName ? (
              <div className="flex items-center gap-3">
                <div className={`p-2 rounded-xl ${selectedToken.bgColor}`}>
                  <selectedToken.icon
                    className={`text-xl ${selectedToken.color}`}
                  />
                </div>
                <div className="text-left">
                  <p className="font-bold text-gray-900">
                    {selectedToken.tokenName}
                  </p>
                  <p className="text-xs text-gray-500 font-medium">
                    {selectedToken.network} Network
                  </p>
                </div>
              </div>
            ) : (
              <span className="text-gray-500 font-medium ml-2">
                Choose payment method...
              </span>
            )}
            <FaChevronDown
              className={`text-gray-400 transition-transform ${
                toggleSelectState ? "rotate-180" : ""
              }`}
            />
          </button>

          {/* Dropdown */}
          {toggleSelectState && (
            <div className="absolute top-full left-0 w-full mt-2 bg-white rounded-2xl shadow-xl border border-gray-100 z-50 overflow-hidden animate-fade-in-down max-h-64 overflow-y-auto">
              {tokensArray.map((token, idx) => (
                <div
                  key={idx}
                  onClick={() => handleSelectToken(token)}
                  className="flex items-center gap-3 p-4 hover:bg-gray-50 cursor-pointer border-b border-gray-50 last:border-0"
                >
                  <div className={`p-2 rounded-full ${token.bgColor}`}>
                    <token.icon className={`text-sm ${token.color}`} />
                  </div>
                  <span className="font-bold text-gray-700 text-sm">
                    {token.tokenName}
                  </span>
                  {selectedToken.shortName === token.shortName && (
                    <FaCheckCircle className="ml-auto text-primary" />
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Amount Input */}
        <div>
          <label className="block text-xs font-bold text-gray-500 uppercase tracking-wide mb-2">
            Amount ({systemCurrency})
          </label>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
              <span className="text-gray-400 font-bold text-lg">
                {getCurrencySymbol()}
              </span>
            </div>
            <input
              type="number"
              value={amount || ""}
              onChange={handleAmountChange}
              placeholder="0.00"
              className="w-full pl-10 pr-4 py-2 text-3xl font-heading font-semibold text-gray-900 bg-white border-2 border-b-blue-700 border-gray-200 rounded-xl focus:border-primary outline-none transition-colors placeholder-gray-200"
            />
          </div>

          <div className="flex items-center justify-between mt-2">
            {error ? (
              <p className="text-xs text-red-500 font-medium">{error}</p>
            ) : (
              <p className="text-xs text-gray-400">
                Min: {getCurrencySymbol()}
                {convertUSDToDisplay(minimunDeposit).toFixed(0)}
              </p>
            )}
            {!isLoaded && (
              <span className="text-xs text-blue-500 animate-pulse">
                Syncing rates...
              </span>
            )}
          </div>
        </div>

        <div className="pt-4">
          <PrimaryButton
            action={() => setPages(1)}
            text="Continue"
            disabled={
              !selectedToken.tokenName ||
              !isLoaded ||
              amount < convertUSDToDisplay(minimunDeposit) ||
              !!error
            }
            className="w-full py-4 text-lg"
          />
        </div>
      </form>
    </div>
  );
};
